import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import RouteTracker from './components/RouteTracker';
import { Users, Box, Truck, Package, BarChart3, Calendar, Server, Code, Database, Settings, Shield, ShieldCheck, GraduationCap, BookText, FileX, Gauge } from 'lucide-react';

// Custom components
import UserMenu from './components/UserMenu';
import ForcedPasswordChange from './components/ForcedPasswordChange';

// Auth provider and Permission provider
import { useAuth } from './context/AuthContext';
import { AuthProvider } from './context/AuthContext';
import { usePermission, PermissionProvider } from './context/PermissionContext';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionRoute from './components/PermissionRoute';
import ForcePasswordChangeRoute from './components/ForcePasswordChangeRoute';

// Auth pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// Components
import SideNav from './components/SideNav';

// Home, Company, Profile, Users and Roles pages
import RecentActivity from './pages/home/RecentActivity';
import Notifications from './pages/home/Notifications';
import CompanyProfile from './pages/company/CompanyProfile';
import Locations from './pages/company/Locations';
import Departments from './pages/company/Departments';
import Billing from './pages/company/Billing';
import Integrations from './pages/company/Integrations';
import MyProfile from './pages/profile/MyProfile';
import Preferences from './pages/profile/Preferences';
import Security from './pages/profile/Security';
import ProfileNotifications from './pages/profile/ProfileNotifications';
import AllUsers from './pages/users/AllUsers';
import UserGroups from './pages/users/UserGroups';
import AccessLogs from './pages/users/AccessLogs';
import RoleManagement from './pages/roles/RoleManagement';
import CreateRole from './pages/roles/CreateRole';
import Permissions from './pages/roles/Permissions';
import RoleAssignment from './pages/roles/RoleAssignment';
import AdminPanel from './pages/admin/AdminPanel';
import WarehouseMetrics from './pages/metrics/WarehouseMetrics';
import Inbound from './pages/inbound/Inbound';
import LMSLayout from './pages/tiles/labor_management_system/LMSLayout';
import ClockIn from './pages/tiles/labor_management_system/general/ClockIn';
import HoursStats from './pages/tiles/labor_management_system/general/HoursStats';
import Profile from './pages/tiles/labor_management_system/general/Profile';
import Dashboard from './pages/tiles/labor_management_system/administration/Dashboard';
import ActivitiesProjects from './pages/tiles/labor_management_system/administration/ActivitiesProjects';
import BusinessUnits from './pages/tiles/labor_management_system/administration/BusinessUnits';
import BirdsEye from './pages/tiles/labor_management_system/administration/BirdsEye';
import AlertsReview from './pages/tiles/labor_management_system/administration/AlertsReview';
import EmergencyRoster from './pages/tiles/labor_management_system/administration/EmergencyRoster';
import ShiftsTeams from './pages/tiles/labor_management_system/administration/ShiftsTeams';
import Command from './pages/tiles/labor_management_system/groups/Command';
import CreateAssign from './pages/tiles/labor_management_system/groups/CreateAssign';
import MyTime from './pages/tiles/labor_management_system/timeManagement/MyTime';
import KioskView from './pages/tiles/labor_management_system/kiosk/KioskView';
import KioskSignup from './pages/tiles/labor_management_system/kiosk/KioskSignup';
import WarehouseMetricsDashboard from './pages/tiles/warehouse_metrics_dashboard/Dashboard';
import DownstockingReport from './pages/tiles/downstocking_report/Dashboard';
import UpdateOrderDueDate from './pages/tiles/update_order_due_date/Dashboard';
import IncommVoidReport from './pages/tiles/incomm_void_report/Dashboard';
import VoidReportLayout from './pages/tiles/incomm_void_report/VoidReportLayout';
import WercMetrics from './pages/tiles/werc_metrics/Dashboard';
import SafetyCalendar from './pages/tiles/safety_calendar/Dashboard';
import SFTPAdministration from './pages/tiles/sftp_administration/Dashboard';
import ScriptManagerOld from './pages/tiles/script_manager';
import MySQLManagement from './pages/tiles/mysql_management/Dashboard';
import CITeamDashboard from './pages/tiles/ci_team/Dashboard';
import LaborForecastingTool from './pages/tiles/labor_forecasting_tool/Dashboard';
import APIDashboard from './pages/tiles/apis/Dashboard';
import APIsLayout from './pages/tiles/apis/APIsLayout';
import SnowflakeKivaAPI from './pages/tiles/apis/SnowflakeKivaAPI';
import LocationInventoryQuery from './pages/tiles/apis/LocationInventoryQuery';
import OrderCycleTimeReport from './pages/tiles/apis/OrderCycleTimeReport';
import OrderDetailsQuery from './pages/tiles/apis/OrderDetailsQuery';
import OrderQuery from './pages/tiles/apis/OrderQuery';
import PickingReport from './pages/tiles/apis/PickingReport';
import SalesOrderReport from './pages/tiles/apis/SalesOrderReport';
import SKUMasterView from './pages/tiles/apis/SKUMasterView';
import ViewReceiptDetail from './pages/tiles/apis/ViewReceiptDetail';
// Client Facing Tools - Admin Control Center
import ClientFacingTools from './pages/tiles/client_facing_tools/Dashboard';
import CFTLayout from './pages/tiles/client_facing_tools/CFTLayout';
import ClientUsers from './pages/tiles/client_facing_tools/ClientUsers';
import CFTBusinessUnits from './pages/tiles/client_facing_tools/BusinessUnits';
import Analytics from './pages/tiles/client_facing_tools/Analytics';
import PortalSettings from './pages/tiles/client_facing_tools/PortalSettings';
import AccessControl from './pages/tiles/client_facing_tools/AccessControl';

// KSP Training tile
import TrainingDashboard from './pages/tiles/ksp_training/TrainingDashboard';
import TrainingViewer from './pages/tiles/ksp_training/TrainingViewer';
import TrainingAdminPanel from './pages/tiles/ksp_training/admin/AdminPanel';
import TrainingLayout from './pages/tiles/ksp_training/TrainingLayout';
import MyHistory from './pages/tiles/ksp_training/MyHistory';
import SurveyList from './pages/tiles/ksp_training/surveys/SurveyList';
import SurveyViewer from './pages/tiles/ksp_training/surveys/SurveyViewer';

// KSP SOPs tile
import SopLayout from './pages/tiles/ksp_sops/SopLayout';
import SopLibrary from './pages/tiles/ksp_sops/SopLibrary';
import SopViewer from './pages/tiles/ksp_sops/SopViewer';
import SopEditor from './pages/tiles/ksp_sops/SopEditor';
import SopRecent from './pages/tiles/ksp_sops/SopRecent';
import SopTagManager from './pages/tiles/ksp_sops/admin/TagManager';
import SopSearchInsights from './pages/tiles/ksp_sops/admin/SearchInsights';

// Client Portal - For Client Users
import ClientPortalLayout from './pages/client-portal/ClientPortalLayout';
import ClientDashboard from './pages/client-portal/ClientDashboard';
import ClientPages from './pages/client-portal/ClientPages';
import ClientFileShare from './pages/client-portal/ClientFileShare';
import ClientTickets from './pages/client-portal/ClientTickets';
import SystemConfigLayout from './pages/tiles/system_configuration/SystemConfigLayout';
import SFTPUserManagement from './pages/tiles/system_configuration/sftp/SFTPUserManagement';
import SFTPDirectoryManagement from './pages/tiles/system_configuration/sftp/SFTPDirectoryManagement';
import SFTPIPAccessControl from './pages/tiles/system_configuration/sftp/SFTPIPAccessControl';
import SFTPServerStatus from './pages/tiles/system_configuration/sftp/SFTPServerStatus';
import ScriptManager from './pages/tiles/system_configuration/scripts/ScriptManager';
import ExecutionMonitor from './pages/tiles/system_configuration/scripts/ExecutionMonitor';
import LogViewer from './pages/tiles/system_configuration/scripts/LogViewer';
import ScriptSettings from './pages/tiles/system_configuration/scripts/ScriptSettings';
import SchemaManager from './pages/tiles/system_configuration/database/SchemaManager';
import ReplicationSetup from './pages/tiles/system_configuration/database/ReplicationSetup';
import QueryConsole from './pages/tiles/system_configuration/database/QueryConsole';
import PerformanceMonitor from './pages/tiles/system_configuration/database/PerformanceMonitor';
import ProxyConfiguration from './pages/tiles/system_configuration/proxy/ProxyConfiguration';
import SSLCertificates from './pages/tiles/system_configuration/proxy/SSLCertificates';
import LoadBalancing from './pages/tiles/system_configuration/proxy/LoadBalancing';
import ProxyAccessLogs from './pages/tiles/system_configuration/proxy/AccessLogs';
import VPNManagement from './pages/tiles/system_configuration/proxy/VPNManagement';
import { InboundTruckRoutes, OutboundTruckRoutes } from './pages/tiles/truck_scheduler/routes';
import { AnnouncementProvider } from './context/AnnouncementContext';
import AnnouncementBanner from './components/announcements/AnnouncementBanner';
import AnnouncementModal from './components/announcements/AnnouncementModal';
import ManageAnnouncements from './pages/announcements/ManageAnnouncements';

// Client Portal Components (removed - now using simplified client_facing_tools)

// Auth-aware redirect component for handling 404/unknown routes
// MODIFIED: Only redirects unauthenticated users to login page
const AuthAwareRedirect = () => {
  const { isAuthenticated, authLoading } = useAuth();
  
  // If auth is still loading, render nothing until it's done
  if (authLoading) {
    console.log('🔒 Auth: Still loading, delaying redirect decision');
    return null;
  }
  
  console.log('🔒 Auth: Redirect check, authenticated:', isAuthenticated);
  
  // Only redirect unauthenticated users to login
  if (!isAuthenticated) {
    console.log('🔒 Auth: Unauthenticated user, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  // For all other cases, just return null and let the router handle it
  console.log('🔒 Auth: User is authenticated, no redirect needed');
  return null;
};


// Main Layout Component
const MainLayout = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(true);

  return (
    <div className={`flex flex-col lg:flex-row min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
      <SideNav />
      <div className="flex-1 w-full lg:w-auto flex flex-col overflow-hidden"> {/* Critical for proper height constraints */}
        <header className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} 
          border-b sticky top-0 z-30 flex-shrink-0`}> {/* flex-shrink-0 keeps header fixed size */}
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}
              ml-12 lg:ml-0 whitespace-nowrap flex-shrink-0`}>
              KSP <span className="hidden sm:inline">| Headquarters</span>
            </h1>

            <AnnouncementBanner />

            <div className="flex-shrink-0">
              <UserMenu isDarkMode={isDarkMode} />
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-6 flex-1 overflow-hidden"> {/* Critical for proper height constraints */}
          {children}
        </main>
      </div>
    </div>
  );
};

// Dashboard Content Component
const DashboardContent = () => {
  const { hasPermission, userPermissions, loading, error } = usePermission();
  const { currentUser } = useAuth();
  
  console.log('🏠 Dashboard: Loading permissions data. Loading:', loading, 'Error:', error);
  console.log('🏠 Dashboard: Current user:', currentUser);
  console.log('🏠 Dashboard: Available permissions:', userPermissions);
  
  // Log any problems with the permission system
  React.useEffect(() => {
    if (!loading && userPermissions.length === 0) {
      console.log('🏠 Dashboard: No permissions loaded but loading is complete!');
      console.log('🏠 Dashboard: This could indicate a problem with the permission system.');
      
      // Debug info about the user's roles
      if (currentUser?.roles) {
        console.log('🏠 Dashboard: User has roles:', currentUser.roles);
        const isAdmin = currentUser.roles.some(role => role.name === 'Admin');
        console.log('🏠 Dashboard: User is admin according to roles:', isAdmin);
      }
    }
  }, [loading, userPermissions, currentUser]);
  
  const systems = [
    {
      title: 'Labor Management System',
      icon: <Users className="w-12 h-12 mb-4" />,
      description: 'Manage workforce scheduling, time tracking, and performance metrics.',
      path: '/tiles/labor_management_system/general/clock-in',
      color: 'bg-blue-600 hover:bg-blue-700',
      external: false,
      permission: 'labor_management_system'
    },
    {
      title: 'Warehouse Metrics Dashboard',
      icon: <Box className="w-12 h-12 mb-4" />,
      description: 'Warehouse metrics system.',
      path: 'https://dash.ksp3plhq.com',
      color: 'bg-green-600 hover:bg-green-700',
      external: true,
      permission: 'warehouse_metrics'
    },
    {
      title: 'Truck Scheduler',
      icon: <Truck className="w-12 h-12 mb-4" />,
      description: 'Comprehensive truck scheduling system for inbound and outbound operations.',
      path: '/tiles/truck_scheduler/inbound/calendar',
      color: 'bg-blue-600 hover:bg-blue-700',
      external: false,
      permission: 'truck_scheduler' // Use consistent naming for all truck scheduler operations
    },
    {
      title: 'Down Stocking Report',
      icon: <BarChart3 className="w-12 h-12 mb-4" />,
      description: 'Calculate and publish the Down Stocking Report.',
      path: '/tiles/downstocking_report/dashboard',
      color: 'bg-purple-600 hover:bg-purple-700',
      external: false,
      permission: 'downstocking_report'
    },
    {
      title: 'Update Order Due Date & Accuracy',
      icon: <Calendar className="w-12 h-12 mb-4" />,
      description: 'Update Due Dates on orders to allow KPI\'s to reflect accurately.',
      path: '/tiles/update_order_due_date/dashboard',
      color: 'bg-indigo-600 hover:bg-indigo-700',
      external: false,
      permission: 'update_order_due_date'
    },
    {
      title: 'Incomm VOID Report',
      icon: <FileX className="w-12 h-12 mb-4" />,
      description: 'Pull and export voided shipping labels and their replacements.',
      path: '/tiles/incomm_void_report/dashboard',
      color: 'bg-rose-600 hover:bg-rose-700',
      external: false,
      permission: 'incomm_void_report'
    },
    {
      title: 'KSP Insights',
      icon: <Gauge className="w-12 h-12 mb-4" />,
      description: 'KSP operations and WERC performance metrics dashboard.',
      path: '/tiles/werc_metrics/dashboard',
      color: 'bg-emerald-600 hover:bg-emerald-700',
      external: false,
      permission: 'werc_metrics'
    },
    {
      title: 'System Configuration',
      icon: <Settings className="w-12 h-12 mb-4" />,
      description: 'Unified administration panel for SFTP, Scripts, and Database management.',
      path: '/tiles/system_configuration/sftp/users',
      color: 'bg-slate-600 hover:bg-slate-700',
      external: false,
      permission: 'system_configuration'
    },
    {
      title: 'Client Facing Tools',
      icon: <Settings className="w-12 h-12 mb-4" />,
      description: 'Manage tools and interfaces available to your clients.',
      path: '/tiles/client_facing_tools',
      color: 'bg-teal-600 hover:bg-teal-700',
      external: false,
      permission: 'client_facing_tools'
    },
    {
      title: 'C.I. Team',
      icon: <Shield className="w-12 h-12 mb-4" />,
      description: 'Confidential investigations and security operations dashboard.',
      path: '/tiles/ci_team/dashboard',
      color: 'bg-slate-800 hover:bg-slate-900',
      external: false,
      permission: 'ci_team'
    },
    {
      title: 'Labor Forecasting Tool',
      icon: <Users className="w-12 h-12 mb-4" />,
      description: 'Forecast labor needs and optimize workforce planning.',
      path: '/tiles/labor_forecasting_tool/dashboard',
      color: 'bg-orange-600 hover:bg-orange-700',
      external: false,
      permission: 'labor_forecasting_tool'
    },
    {
      title: 'APIs',
      icon: <Code className="w-12 h-12 mb-4" />,
      description: 'API management and configuration dashboard for administrators.',
      path: '/tiles/apis/dashboard',
      color: 'bg-cyan-600 hover:bg-cyan-700',
      external: false,
      permission: 'apis'
    },
    {
      title: 'KSP Training',
      icon: <GraduationCap className="w-12 h-12 mb-4" />,
      description: 'Required and optional training modules for KSP staff.',
      path: '/tiles/ksp_training/dashboard',
      color: 'bg-amber-600 hover:bg-amber-700',
      external: false,
      permission: 'ksp_training'
    },
    {
      title: 'Safety Calendar',
      icon: <ShieldCheck className="w-12 h-12 mb-4" />,
      description: 'Log workplace safety status, track OSHA reportable injuries, and export the incident log.',
      path: '/tiles/safety_calendar/dashboard',
      color: 'bg-red-600 hover:bg-red-700',
      external: false,
      permission: 'safety_calendar'
    },
    {
      title: 'KSP SOPs',
      icon: <BookText className="w-12 h-12 mb-4" />,
      description: 'Search and read company Standard Operating Procedures in any format.',
      path: '/tiles/ksp_sops/library',
      color: 'bg-indigo-600 hover:bg-indigo-700',
      external: false,
      // Every logged-in user sees SOPs, regardless of role permissions.
      alwaysVisible: true
    }
  ];

  // Filter systems by user permissions
  // Log each permission check for debugging
  const authorizedSystems = systems.filter(system => {
    const hasAccess = system.alwaysVisible || hasPermission(system.permission);
    console.log(`🏠 Dashboard: Permission check for ${system.title} (${system.permission}):`, hasAccess);
    return hasAccess;
  });
  
  // If still loading, show loading indicator
  if (loading) {
    return (
      <div className="rounded-lg shadow-sm border p-6 bg-slate-800 border-slate-700">
        <div className="text-center py-12">
          <div className="text-slate-400 mb-4">Loading dashboard tiles...</div>
          <div className="w-12 h-12 border-t-4 border-blue-500 rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }
  
  // If error loading permissions, show error message
  if (error) {
    return (
      <div className="rounded-lg shadow-sm border p-6 bg-slate-800 border-slate-700">
        <div className="text-center py-12">
          <div className="text-red-400 mb-4">Error loading permissions: {error}</div>
          <div className="text-slate-500 text-sm">Please try refreshing the page or contact support.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg shadow-sm border p-6 bg-slate-800 border-slate-700">
      {authorizedSystems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {authorizedSystems.map((system) => (
            system.external ? (
              <a 
                key={system.title}
                href={system.path}
                target="_blank"
                rel="noopener noreferrer"
                className={`${system.color} p-6 rounded-lg shadow-lg transform transition-all duration-200 
                            hover:scale-105 text-white text-center`}
              >
                <div className="flex flex-col items-center">
                  {system.icon}
                  <h2 className="text-xl font-bold mb-2">{system.title}</h2>
                  <p className="text-sm opacity-90">{system.description}</p>
                </div>
              </a>
            ) : (
              <Link 
                key={system.title}
                to={system.path}
                className={`${system.color} p-6 rounded-lg shadow-lg transform transition-all duration-200 
                            hover:scale-105 text-white text-center`}
              >
                <div className="flex flex-col items-center">
                  {system.icon}
                  <h2 className="text-xl font-bold mb-2">{system.title}</h2>
                  <p className="text-sm opacity-90">{system.description}</p>
                </div>
              </Link>
            )
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-slate-400 mb-4">You don't have access to any dashboard tiles.</div>
          <div className="text-slate-500 text-sm">Please contact your administrator if you need access.</div>
          <button 
            onClick={() => window.debugPermissions()} 
            className="mt-6 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded"
          >
            Debug Permissions
          </button>
        </div>
      )}
    </div>
  );
};

// Enhanced utility function to handle page refreshes
// Improved to handle permission loading race condition
const handlePageRefresh = () => {
  console.log('🔄 Refresh handler: ENABLED - Intelligent route restoration active');
  
  // Set a flag indicating this is a page refresh - will be used by PermissionRoute
  sessionStorage.setItem('fromPageRefresh', 'true');
  
  // Get all stored path information from sessionStorage
  const lastPath = sessionStorage.getItem('last_path');
  const lastRoute = sessionStorage.getItem('lastRoute');
  const lastProtectedRoute = sessionStorage.getItem('lastAttemptedProtectedRoute');
  
  // Use the protected route as priority if available (this comes from permission system)
  const bestRoute = lastProtectedRoute || lastRoute || lastPath;
  
  console.log('🔄 Route restoration options:', {
    lastPath,
    lastRoute,
    lastProtectedRoute,
    currentPath: window.location.pathname
  });
  
  // Only attempt restoration if we have a path, we're at the root, and no restoration is in progress
  const isRoot = window.location.pathname === '/';
  const shouldRestore = bestRoute && isRoot;
  
  if (shouldRestore) {
    // Set a flag to indicate restoration is in progress (to prevent RouteTracker from overwriting)
    sessionStorage.setItem('routeRestorationInProgress', 'true');
    
    console.log('🔄 Restoring to route:', bestRoute);
    
    // Use a more significant delay to ensure auth state and permissions have time to initialize
    setTimeout(() => {
      window.history.replaceState(null, '', bestRoute);
      
      // Clear the restoration flag after a longer delay to ensure navigation completes
      setTimeout(() => {
        sessionStorage.removeItem('routeRestorationInProgress');
        // We keep the 'fromPageRefresh' flag until permissions are fully loaded
        console.log('🔄 Route restoration completed');
      }, 1000);
    }, 300);
  } else {
    console.log('🔄 No route restoration needed');
    // Clear the fromPageRefresh flag after a delay if no restoration was needed
    setTimeout(() => {
      sessionStorage.removeItem('fromPageRefresh');
    }, 5000);
  }
};

function App() {
  // Call the refresh handler when the component mounts
  React.useEffect(() => {
    console.log('🔄 App mounted, handling page refresh');
    
    // Check if this appears to be a refresh
    const isRefresh = window.performance && 
      window.performance.navigation && 
      window.performance.navigation.type === 1;
    
    if (isRefresh) {
      console.log('🔄 Detected browser refresh via performance API');
      sessionStorage.setItem('fromPageRefresh', 'true');
    }
    
    // Delay just enough to let auth state initialize, but don't wait too long
    // We need to restore the route before any permission checks happen
    const delayTime = isRefresh ? 10 : 50;
    setTimeout(handlePageRefresh, delayTime);
    
    // Secondary restoration attempt for race conditions
    setTimeout(() => {
      // If we're on the dashboard, check if we should be elsewhere
      if (window.location.pathname === '/') {
        console.log('🔄 Secondary route check - still on dashboard, running backup restoration');
        handlePageRefresh();
      }
    }, 1000);
  }, []);
  
  return (
    <AuthProvider>
      <PermissionProvider>
        <Router>
        <AnnouncementProvider>
          <RouteTracker />
          <AnnouncementModal />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Public kiosk signup route - token validation handled within component */}
            <Route path="/tiles/labor_management_system/kiosk/signup" element={<KioskSignup />} />
            {/* Admin Panel Route - handles its own authentication */}
            <Route path="/admin" element={<AdminPanel />} />
            
            {/* Old client portal routes removed - now using simplified client_facing_tools */}
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
            <Route element={<ForcePasswordChangeRoute />}>
            {/* Home Routes */}
            <Route path="/" element={
              <MainLayout>
                <DashboardContent />
              </MainLayout>
            } />
            <Route path="/home/activity" element={
              <MainLayout>
                <RecentActivity />
              </MainLayout>
            } />
            <Route path="/home/notifications" element={
              <MainLayout>
                <Notifications />
              </MainLayout>
            } />

            {/* Company Routes - Require company_section permission */}
            <Route element={<PermissionRoute requiredPermission="company_section" redirectTo="/" />}>
              <Route path="/company/profile" element={
                <MainLayout>
                  <CompanyProfile />
                </MainLayout>
              } />
              <Route path="/company/locations" element={
                <MainLayout>
                  <Locations />
                </MainLayout>
              } />
              <Route path="/company/departments" element={
                <MainLayout>
                  <Departments />
                </MainLayout>
              } />
            </Route>
            
            {/* Billing - Require billing_access permission */}
            <Route element={<PermissionRoute requiredPermission="billing_access" redirectTo="/" />}>
              <Route path="/company/billing" element={
                <MainLayout>
                  <Billing />
                </MainLayout>
              } />
            </Route>
            
            {/* Integrations - Require integrations permission */}
            <Route element={<PermissionRoute requiredPermission="integrations" redirectTo="/" />}>
              <Route path="/company/integrations" element={
                <MainLayout>
                  <Integrations />
                </MainLayout>
              } />
            </Route>

            {/* Profile Routes */}
            <Route path="/profile" element={
              <MainLayout>
                <MyProfile />
              </MainLayout>
            } />
            <Route path="/profile/preferences" element={
              <MainLayout>
                <Preferences />
              </MainLayout>
            } />
            <Route path="/profile/security" element={
              <MainLayout>
                <Security />
              </MainLayout>
            } />
            <Route path="/profile/notifications" element={
              <MainLayout>
                <ProfileNotifications />
              </MainLayout>
            } />

            {/* Users Routes - Require view_users permission */}
            <Route element={<PermissionRoute requiredPermission="view_users" redirectTo="/" />}>
              <Route path="/users/all" element={
                <MainLayout>
                  <AllUsers />
                </MainLayout>
              } />
              <Route path="/users/groups" element={
                <MainLayout>
                  <UserGroups />
                </MainLayout>
              } />
            </Route>
            
            {/* Access Logs - Require view_access_logs permission */}
            <Route element={<PermissionRoute requiredPermission="view_access_logs" redirectTo="/" />}>
              <Route path="/users/logs" element={
                <MainLayout>
                  <AccessLogs />
                </MainLayout>
              } />
            </Route>

            {/* Roles Routes - Require manage_roles permission */}
            <Route element={<PermissionRoute requiredPermission="manage_roles" redirectTo="/" />}>
              <Route path="/roles/manage" element={
                <MainLayout>
                  <RoleManagement />
                </MainLayout>
              } />
              <Route path="/roles/create" element={
                <MainLayout>
                  <CreateRole />
                </MainLayout>
              } />
              <Route path="/roles/permissions" element={
                <MainLayout>
                  <Permissions />
                </MainLayout>
              } />
              <Route path="/roles/assign" element={
                <MainLayout>
                  <RoleAssignment />
                </MainLayout>
              } />
            </Route>

            {/* Announcements admin — access is announcement_admins, enforced by the page and API */}
            <Route path="/announcements/manage" element={
              <MainLayout>
                <ManageAnnouncements />
              </MainLayout>
            } />
            
            
            {/* Legacy routes - for backward compatibility */}
            <Route path="/metrics/warehouse" element={
              <MainLayout>
                <WarehouseMetrics />
              </MainLayout>
            } />
            <Route path="/inbound/inbound" element={
              <MainLayout>
                <Inbound />
              </MainLayout>
            } />
            
            {/* Backward compatibility redirects - DISABLED */}
            <Route path="/lms/*" element={
              <div className="min-h-screen flex items-center justify-center bg-slate-900">
                <div className="text-center text-slate-300">
                  <h1 className="text-2xl mb-4">LMS Legacy Route</h1>
                  <p>Redirects have been disabled. Please use the correct route directly:</p>
                  <p className="mt-2 text-blue-400">/tiles/labor_management_system/general/clock-in</p>
                </div>
              </div>
            } />
          </Route>
          </Route>
          
          {/* Tile system routes */}
          
          {/* Labor Management System (LMS) Routes - Require labor_management_system permission */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="labor_management_system" redirectTo="/" />}>
              <Route path="/tiles/labor_management_system" element={
                <LMSLayout><ClockIn /></LMSLayout>
              } />
              
              {/* LMS Clock In - Requires lms_clock_in permission */}
              <Route element={<PermissionRoute requiredPermission="lms_clock_in" redirectTo="/tiles/labor_management_system/general/hours-stats" />}>
                <Route path="/tiles/labor_management_system/general/clock-in" element={<LMSLayout><ClockIn /></LMSLayout>} />
              </Route>
              
              {/* LMS Hours & Stats - Requires lms_hours_stats permission */}
              <Route element={<PermissionRoute requiredPermission="lms_hours_stats" redirectTo="/tiles/labor_management_system/general/profile" />}>
                <Route path="/tiles/labor_management_system/general/hours-stats" element={<LMSLayout><HoursStats /></LMSLayout>} />
              </Route>
              
              {/* LMS Profile - Requires lms_profile permission */}
              <Route element={<PermissionRoute requiredPermission="lms_profile" redirectTo="/" />}>
                <Route path="/tiles/labor_management_system/general/profile" element={<LMSLayout><Profile /></LMSLayout>} />
              </Route>
              
              {/* LMS Time Management - Requires lms_time_management permission */}
              <Route element={<PermissionRoute requiredPermission="lms_time_management" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/time-management/my-time" element={<LMSLayout><MyTime /></LMSLayout>} />
              </Route>
              
              {/* LMS Administration Dashboard - Requires lms_administration permission */}
              <Route element={<PermissionRoute requiredPermission="lms_administration" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/administration/dashboard" element={<LMSLayout><Dashboard /></LMSLayout>} />
              </Route>
              
              {/* LMS Activities & Projects - Requires lms_activities permission */}
              <Route element={<PermissionRoute requiredPermission="lms_activities" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/administration/activities-projects" element={<LMSLayout><ActivitiesProjects /></LMSLayout>} />
              </Route>
              
              {/* LMS Shifts & Teams - Requires lms_shifts_teams permission */}
              <Route element={<PermissionRoute requiredPermission="lms_shifts_teams" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/administration/shifts-teams" element={<LMSLayout><ShiftsTeams /></LMSLayout>} />
              </Route>
              
              {/* LMS Business Units - Requires lms_business_units permission */}
              <Route element={<PermissionRoute requiredPermission="lms_business_units" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/administration/business-units" element={<LMSLayout><BusinessUnits /></LMSLayout>} />
              </Route>
              
              {/* LMS Bird's Eye - Requires lms_birds_eye permission */}
              <Route element={<PermissionRoute requiredPermission="lms_birds_eye" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/administration/birds-eye" element={<LMSLayout><BirdsEye /></LMSLayout>} />
              </Route>
              
              {/* LMS Alerts & Review - Requires lms_administration permission */}
              <Route element={<PermissionRoute requiredPermission="lms_administration" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/administration/alerts-review" element={<LMSLayout><AlertsReview /></LMSLayout>} />
              </Route>
              
              {/* LMS Emergency Roster - Requires lms_administration permission */}
              <Route element={<PermissionRoute requiredPermission="lms_administration" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/administration/emergency-roster" element={<LMSLayout><EmergencyRoster /></LMSLayout>} />
              </Route>
              
              {/* LMS Command - Requires lms_command permission */}
              <Route element={<PermissionRoute requiredPermission="lms_command" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/groups/command" element={<LMSLayout><Command /></LMSLayout>} />
              </Route>
              
              {/* LMS Create & Assign - Requires lms_create_assign permission */}
              <Route element={<PermissionRoute requiredPermission="lms_create_assign" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/groups/create-assign" element={<LMSLayout><CreateAssign /></LMSLayout>} />
              </Route>
              
              {/* LMS Kiosk View - Requires lms_kiosk permission */}
              <Route element={<PermissionRoute requiredPermission="lms_kiosk" redirectTo="/tiles/labor_management_system/general/clock-in" />}>
                <Route path="/tiles/labor_management_system/kiosk/kiosk-view" element={<KioskView />} />
              </Route>
            </Route>
          </Route>
          </Route>
          
          {/* Other Tile Routes */}
          
          {/* Warehouse Metrics Dashboard - Require warehouse_metrics permission */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="warehouse_metrics" redirectTo="/" />}>
              <Route path="/tiles/warehouse_metrics_dashboard/dashboard" element={<WarehouseMetricsDashboard />} />
            </Route>
          </Route>
          </Route>
          
          {/* Truck Scheduler - Use unified truck_scheduler permission with backward compatibility */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute 
              requiredPermission="truck_scheduler"
              redirectTo="/" 
            />}>
              {/* Updated paths for new truck scheduler structure */}
              <Route path="/tiles/truck_scheduler/inbound/*" element={<InboundTruckRoutes />} />
              <Route path="/tiles/truck_scheduler/outbound/*" element={<OutboundTruckRoutes />} />
              
              {/* Legacy paths for backward compatibility - DISABLED */}
              <Route path="/tiles/inbound_truck_schedule/*" element={
                <div className="min-h-screen flex items-center justify-center bg-slate-900">
                  <div className="text-center text-slate-300">
                    <h1 className="text-2xl mb-4">Legacy Truck Scheduler Route</h1>
                    <p>Redirects have been disabled. Please use the correct route directly:</p>
                    <p className="mt-2 text-blue-400">/tiles/truck_scheduler/inbound/calendar</p>
                  </div>
                </div>
              } />
              <Route path="/tiles/outbound_truck_schedule/*" element={
                <div className="min-h-screen flex items-center justify-center bg-slate-900">
                  <div className="text-center text-slate-300">
                    <h1 className="text-2xl mb-4">Legacy Truck Scheduler Route</h1>
                    <p>Redirects have been disabled. Please use the correct route directly:</p>
                    <p className="mt-2 text-blue-400">/tiles/truck_scheduler/outbound/calendar</p>
                  </div>
                </div>
              } />
            </Route>
          </Route>
          </Route>
          
          {/* Downstocking Report - Require downstocking_report permission */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="downstocking_report" redirectTo="/" />}>
              <Route path="/tiles/downstocking_report/dashboard" element={
                <MainLayout>
                  <DownstockingReport />
                </MainLayout>
              } />
            </Route>
          </Route>
          </Route>
          
          {/* Update Order Due Date - Require update_order_due_date permission */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="update_order_due_date" redirectTo="/" />}>
              <Route path="/tiles/update_order_due_date/dashboard" element={
                <MainLayout>
                  <UpdateOrderDueDate />
                </MainLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* Incomm VOID Report - Require incomm_void_report permission */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="incomm_void_report" redirectTo="/" />}>
              <Route path="/tiles/incomm_void_report/dashboard" element={
                <VoidReportLayout>
                  <IncommVoidReport />
                </VoidReportLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* KSP Insights - Require werc_metrics permission. VoidReportLayout is a
              generic h-screen shell, so the iframe can fill the content area. */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="werc_metrics" redirectTo="/" />}>
              <Route path="/tiles/werc_metrics/dashboard" element={
                <VoidReportLayout>
                  <WercMetrics />
                </VoidReportLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* Safety Calendar - Require safety_calendar permission */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="safety_calendar" redirectTo="/" />}>
              <Route path="/tiles/safety_calendar/dashboard" element={
                <MainLayout>
                  <SafetyCalendar />
                </MainLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* KSP Training - Require ksp_training permission */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="ksp_training" redirectTo="/" />}>
              <Route path="/tiles/ksp_training/dashboard" element={
                <TrainingLayout>
                  <TrainingDashboard />
                </TrainingLayout>
              } />
              <Route path="/tiles/ksp_training/history" element={
                <TrainingLayout>
                  <MyHistory />
                </TrainingLayout>
              } />
              <Route path="/tiles/ksp_training/take/:trainingId" element={
                <TrainingLayout>
                  <TrainingViewer />
                </TrainingLayout>
              } />
              <Route path="/tiles/ksp_training/surveys" element={
                <TrainingLayout>
                  <SurveyList />
                </TrainingLayout>
              } />
              <Route path="/tiles/ksp_training/survey/:surveyId" element={
                <TrainingLayout>
                  <SurveyViewer />
                </TrainingLayout>
              } />
              <Route path="/tiles/ksp_training/admin/*" element={
                <TrainingLayout>
                  <TrainingAdminPanel />
                </TrainingLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* KSP SOPs - three tiers:
                (any login)      read the library — deliberately ungated; all
                                 users see SOPs no matter what their role
                ksp_sops_author  create / edit (author OR manage satisfies this)
                ksp_sops_manage  tag manager + search insights
              These route guards are for UX. PermissionRoute intentionally falls
              open during page-load and refresh windows, so the real enforcement
              is the matching hasPermission() middleware on /api/sops. */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route>
              <Route path="/tiles/ksp_sops/library" element={
                <SopLayout>
                  <SopLibrary />
                </SopLayout>
              } />
              <Route path="/tiles/ksp_sops/recent" element={
                <SopLayout>
                  <SopRecent />
                </SopLayout>
              } />
              <Route path="/tiles/ksp_sops/favorites" element={
                <SopLayout>
                  <SopLibrary mode="favorites" />
                </SopLayout>
              } />
              <Route path="/tiles/ksp_sops/view/:sopId" element={
                <SopLayout>
                  <SopViewer />
                </SopLayout>
              } />
            </Route>

            {/* Authoring — a manager holds edit rights without needing the
                author code, so either one grants access (requireAll={false}). */}
            <Route element={
              <PermissionRoute
                requiredPermission={['ksp_sops_author', 'ksp_sops_manage']}
                requireAll={false}
                redirectTo="/tiles/ksp_sops/library"
              />
            }>
              <Route path="/tiles/ksp_sops/new" element={
                <SopLayout>
                  <SopEditor />
                </SopLayout>
              } />
              <Route path="/tiles/ksp_sops/edit/:sopId" element={
                <SopLayout>
                  <SopEditor />
                </SopLayout>
              } />
            </Route>

            {/* Administration */}
            <Route element={
              <PermissionRoute requiredPermission="ksp_sops_manage" redirectTo="/tiles/ksp_sops/library" />
            }>
              <Route path="/tiles/ksp_sops/admin/tags" element={
                <SopLayout>
                  <SopTagManager />
                </SopLayout>
              } />
              <Route path="/tiles/ksp_sops/admin/insights" element={
                <SopLayout>
                  <SopSearchInsights />
                </SopLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* System Configuration - Unified admin panel */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="system_configuration" redirectTo="/" />}>
              {/* SFTP Management Routes */}
              <Route path="/tiles/system_configuration/sftp/users" element={
                <SystemConfigLayout>
                  <SFTPUserManagement />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/sftp/directories" element={
                <SystemConfigLayout>
                  <SFTPDirectoryManagement />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/sftp/ip-control" element={
                <SystemConfigLayout>
                  <SFTPIPAccessControl />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/sftp/status" element={
                <SystemConfigLayout>
                  <SFTPServerStatus />
                </SystemConfigLayout>
              } />
              
              {/* Script Management Routes */}
              <Route path="/tiles/system_configuration/scripts/manager" element={
                <SystemConfigLayout>
                  <ScriptManager />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/scripts/monitor" element={
                <SystemConfigLayout>
                  <ExecutionMonitor />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/scripts/logs" element={
                <SystemConfigLayout>
                  <LogViewer />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/scripts/settings" element={
                <SystemConfigLayout>
                  <ScriptSettings />
                </SystemConfigLayout>
              } />
              
              {/* Database Administration Routes */}
              <Route path="/tiles/system_configuration/database/schema" element={
                <SystemConfigLayout>
                  <SchemaManager />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/database/replication" element={
                <SystemConfigLayout>
                  <ReplicationSetup />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/database/console" element={
                <SystemConfigLayout>
                  <QueryConsole />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/database/performance" element={
                <SystemConfigLayout>
                  <PerformanceMonitor />
                </SystemConfigLayout>
              } />
              
              {/* Proxy Management Routes */}
              <Route path="/tiles/system_configuration/proxy/config" element={
                <SystemConfigLayout>
                  <ProxyConfiguration />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/proxy/ssl" element={
                <SystemConfigLayout>
                  <SSLCertificates />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/proxy/load-balancing" element={
                <SystemConfigLayout>
                  <LoadBalancing />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/proxy/logs" element={
                <SystemConfigLayout>
                  <ProxyAccessLogs />
                </SystemConfigLayout>
              } />
              <Route path="/tiles/system_configuration/proxy/vpn" element={
                <SystemConfigLayout>
                  <VPNManagement />
                </SystemConfigLayout>
              } />
            </Route>
          </Route>
          </Route>
          
          {/* Client Facing Tools - Admin Control Center */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="client_facing_tools" redirectTo="/" />}>
              {/* Admin control center for managing client portals */}
              <Route path="/tiles/client_facing_tools" element={
                <CFTLayout>
                  <ClientFacingTools />
                </CFTLayout>
              } />
              <Route path="/tiles/client_facing_tools/client-users" element={
                <CFTLayout>
                  <ClientUsers />
                </CFTLayout>
              } />
              <Route path="/tiles/client_facing_tools/business-units" element={
                <CFTLayout>
                  <CFTBusinessUnits />
                </CFTLayout>
              } />
              <Route path="/tiles/client_facing_tools/analytics" element={
                <CFTLayout>
                  <Analytics />
                </CFTLayout>
              } />
              <Route path="/tiles/client_facing_tools/settings" element={
                <CFTLayout>
                  <PortalSettings />
                </CFTLayout>
              } />
              <Route path="/tiles/client_facing_tools/access" element={
                <CFTLayout>
                  <AccessControl />
                </CFTLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* C.I. Team - Require ci_team permission (admin-only) */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="ci_team" redirectTo="/" />}>
              <Route path="/tiles/ci_team/dashboard" element={<CITeamDashboard />} />
            </Route>
          </Route>
          </Route>

          {/* Labor Forecasting Tool - Require labor_forecasting_tool permission */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="labor_forecasting_tool" redirectTo="/" />}>
              <Route path="/tiles/labor_forecasting_tool/dashboard" element={
                <MainLayout>
                  <LaborForecastingTool />
                </MainLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* APIs - Require apis permission (Admin-only) */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route element={<PermissionRoute requiredPermission="apis" redirectTo="/" />}>
              <Route path="/tiles/apis/dashboard" element={
                <APIsLayout>
                  <APIDashboard />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/snowflake-kiva" element={
                <APIsLayout>
                  <SnowflakeKivaAPI />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/location-inventory" element={
                <APIsLayout>
                  <LocationInventoryQuery />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/order-cycle-time" element={
                <APIsLayout>
                  <OrderCycleTimeReport />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/order-details-query" element={
                <APIsLayout>
                  <OrderDetailsQuery />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/order-query" element={
                <APIsLayout>
                  <OrderQuery />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/picking-report" element={
                <APIsLayout>
                  <PickingReport />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/sales-order-report" element={
                <APIsLayout>
                  <SalesOrderReport />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/sku-master-view" element={
                <APIsLayout>
                  <SKUMasterView />
                </APIsLayout>
              } />
              <Route path="/tiles/apis/view-receipt-detail" element={
                <APIsLayout>
                  <ViewReceiptDetail />
                </APIsLayout>
              } />
            </Route>
          </Route>
          </Route>

          {/* Client Portal Routes - For Client Users Only */}
          <Route element={<ProtectedRoute />}>
          <Route element={<ForcePasswordChangeRoute />}>
            <Route path="/client-portal/:businessUnitId" element={
              <ClientPortalLayout>
                <ClientDashboard />
              </ClientPortalLayout>
            } />
            <Route path="/client-portal/:businessUnitId/pages" element={
              <ClientPortalLayout>
                <ClientPages />
              </ClientPortalLayout>
            } />
            <Route path="/client-portal/:businessUnitId/file-share" element={
              <ClientPortalLayout>
                <ClientFileShare />
              </ClientPortalLayout>
            } />
            <Route path="/client-portal/:businessUnitId/tickets" element={
              <ClientPortalLayout>
                <ClientTickets />
              </ClientPortalLayout>
            } />
          </Route>
          </Route>
          
                    
          {/* Catch-all route - redirect based on authentication status */}
          <Route path="*" element={
            <AuthAwareRedirect />
          } />
        </Routes>
        </AnnouncementProvider>
      </Router>
      </PermissionProvider>
    </AuthProvider>
  );
}

// Add utility functions to App.js
window.debugRouting = function() {
  console.log({
    currentPath: window.location.pathname,
    lastPath: sessionStorage.getItem('last_path'),
    lastRoute: sessionStorage.getItem('lastRoute'),
    localStorage: {
      token: !!localStorage.getItem('token'),
      user: !!localStorage.getItem('user')
    },
    sessionStorage: Object.keys(sessionStorage).reduce((acc, key) => {
      acc[key] = sessionStorage.getItem(key);
      return acc;
    }, {})
  });
};

// Add a function to manually set dashboard permissions for testing
window.setDashboardPermissions = function(enabled = true) {
  // Get the PermissionContext from React DevTools
  try {
    // This is a direct reference to the internal React tree, use with caution!
    const permissionContext = document.querySelector('#root').__reactFiber$?.child?.child?.child?.memoizedState?.context?._currentValue;
    
    if (!permissionContext || !permissionContext.userPermissions) {
      console.log('Could not find PermissionContext - try running this after the app is fully loaded');
      return;
    }
    
    // Make a copy of current permissions
    const currentPermissions = [...permissionContext.userPermissions];
    
    // Dashboard permissions
    const dashboardPermissions = [
      'labor_management_system', 'warehouse_metrics', 'truck_scheduler', 
      'downstocking_report', 'update_order_due_date', 'system_configuration'
    ];
    
    if (enabled) {
      // Add dashboard permissions if they don't exist
      dashboardPermissions.forEach(perm => {
        if (!currentPermissions.includes(perm)) {
          currentPermissions.push(perm);
        }
      });
      
      console.log('Added dashboard permissions:', dashboardPermissions);
    } else {
      // Remove dashboard permissions
      const filteredPermissions = currentPermissions.filter(p => !dashboardPermissions.includes(p));
      console.log('Removed dashboard permissions');
      
      // Set the filtered permissions
      permissionContext.setUserPermissions(filteredPermissions);
      return;
    }
    
    // Set the updated permissions
    permissionContext.setUserPermissions(currentPermissions);
    console.log('Updated permissions:', currentPermissions);
    
    // Force re-render the app
    console.log('Forcing re-render...');
    setTimeout(() => {
      window.location.href = '/';
    }, 500);
  } catch (err) {
    console.error('Error setting permissions:', err);
  }
};

// Enhanced debug function for permissions with specific truck scheduler checks
window.debugPermissions = function() {
  // Get token
  const token = localStorage.getItem('token');
  if (!token) {
    console.log('No auth token found');
    return;
  }
  
  console.log('=== PERMISSION DIAGNOSTIC UTILITY ===');
  console.log('This utility helps diagnose permission issues.');
  
  // Decode token
  try {
    const parts = token.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]));
      console.log('Token payload:', payload);
      console.log('User ID:', payload.id);
      console.log('Roles in token:', payload.roles);
    }
  } catch (err) {
    console.log('Error decoding token:', err);
  }
  
  // Get user from localStorage
  try {
    const userJSON = localStorage.getItem('user');
    if (userJSON) {
      const user = JSON.parse(userJSON);
      console.log('User from localStorage:', user);
      console.log('User roles:', user.roles);
      
      // Check for admin
      if (user.roles && Array.isArray(user.roles)) {
        const isAdmin = user.roles.some(role => role.name === 'Admin');
        console.log('User is admin:', isAdmin);
      }
    }
  } catch (err) {
    console.log('Error parsing user:', err);
  }
  
  // Make actual API call to get permissions
  if (token) {
    console.log('🔄 Making API call to /api/permissions/my-permissions');
    const API_URL = process.env.REACT_APP_API_URL || 
      (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');
    
    fetch(`${API_URL}/permissions/my-permissions`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      console.log('🔄 API response status:', response.status);
      return response.text();
    })
    .then(text => {
      console.log('🔄 Raw API response:', text);
      const data = JSON.parse(text);
      console.log('🔄 Parsed permissions:', data);
      
      if (data && Array.isArray(data.permissions)) {
        const permissionCodes = data.permissions.map(p => p.permission_code);
        console.log('🔄 Permission codes:', permissionCodes);
        
        // Specific check for truck_scheduler permission
        const hasTruckSchedulerPermission = permissionCodes.includes('truck_scheduler');
        console.log('🔄 Has truck_scheduler permission:', hasTruckSchedulerPermission);
        
        if (!hasTruckSchedulerPermission) {
          console.error('🔴 PERMISSION ISSUE DETECTED: User does not have the truck_scheduler permission!');
          console.log('This is required for using both inbound and outbound truck scheduler functionality.');
          console.log('The user will not be able to schedule trucks without this permission.');
        } else {
          console.log('✅ User has truck_scheduler permission - should be able to schedule trucks.');
        }
        
        // Check for dashboard permissions
        const dashboardPermissions = [
          'labor_management_system', 'warehouse_metrics', 'truck_scheduler', 
          'downstocking_report', 'update_order_due_date', 'system_configuration'
        ];
        const hasDashboardPermissions = dashboardPermissions.some(p => permissionCodes.includes(p));
        console.log('🔄 Has dashboard permissions:', hasDashboardPermissions);
        
        // Check for potential legacy permissions
        const hasLegacyPermissions = permissionCodes.includes('inbound_truck_scheduler') || 
                                    permissionCodes.includes('outbound_truck_scheduler');
        if (hasLegacyPermissions) {
          console.warn('⚠️ User has legacy truck scheduler permissions that are no longer used:');
          console.log('The application now uses only "truck_scheduler" permission for all truck operations.');
        }
      }
    })
    .catch(err => {
      console.log('🔄 Error fetching permissions:', err);
    });
  }
  
  console.log('To use this utility in another session, type: window.debugPermissions()');
};

// Utility function to log redirect status
window.logRedirectStatus = function() {
  console.log('REDIRECT MECHANISMS ENABLED:');
  console.log('1. Route persistence is active to maintain page state on refresh');
  console.log('2. Route maintenance code in refresh-helper.js is enabled');
  console.log('3. handlePageRefresh function in App.js is enabled');
  console.log('4. Session storage route tracking is enabled');
  console.log('AUTHENTICATION REDIRECTS ALSO ACTIVE:');
  console.log('- ProtectedRoute.js redirects unauthenticated users to login');
  console.log('- AuthAwareRedirect redirects only unauthenticated users to login');
};

// Log redirect status on page load
console.log('✅ REDIRECT MECHANISMS ENABLED - Route persistence active alongside authentication redirects');

export default App;
